/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError, AxiosResponse } from 'axios';
import { ReturnShippingLabelAPI } from '../api/returns';
import { IErrorReturnService, IGenericError } from '../models/errors';
import {
  IPrintLabelError,
  MilitaryExchange,
  PrintLabelCallbacks,
  PrintLabelParams,
  TrackingDetail,
} from '../models/returns';
import {
  TagPrintReturnLabelBrowserError,
  TagShippingLabelButtonSubmission,
} from './analytics-tagging';
import { isSsr } from './general';
import { getAPIErrorData } from './api-helpers';
import { IReturnShippingLabelResponse } from '../models/api';

export const openNewWindow = (content: string): Window | null => {
  let newWindow: any;
  if (!isSsr) {
    newWindow = window.open(
      '',
      '_blank',
      'top=0,left=0,height=750px,width=auto,scrollbars=yes,resizable=yes',
    );
  }

  if (newWindow) {
    newWindow.document.open();
    newWindow.document.write(content);
    return newWindow;
  }

  return null;
};

export const createHtmlContent = (
  conversionSrcArray: string[],
  orderId: string,
): string => `
  <!DOCTYPE html PUBLIC>
  <html>
    <head>
      <title>Return Label | Order #${orderId}</title>
      <style>
      @page {
        size: landscape;
      }
      </style>
    </head>
    <body style='text-align:center'>
      <div id='imgDiv' style='display:block'>
        ${conversionSrcArray
          .map(
            (conversionSrc, index) =>
              `<img src='${conversionSrc}' alt='Label ${index + 1}' /><br />${
                index !== conversionSrcArray.length - 1
                  ? '<div style="page-break-after: always;"></div>'
                  : ''
              }`,
          )
          .join('')}
      </div>
      <script>
        const printLabel = () => {
          window.print();
        };

        // Print the labels after a delay to allow the images to load
        setTimeout(printLabel, 1000);
      </script>
    </body>
  </html>
`;

export const getErrorBySSRCode = (
  error: IErrorReturnService,
): IPrintLabelError => {
  switch (error.code) {
    case 'SSR_UPSPRNTLBL_ERR_1003':
      // Non UPS Tracking Number Error
      return {
        type: 'TEMPORARY',
        retryable: error.retryable,
      };
    case 'SSR_UPSPRNTLBL_ERR_1012':
      // Label Not Found Error
      return {
        type: 'TRYAGAIN',
        retryable: error.retryable,
      };
    case 'SSR_UPSPRNTLBL_ERR_1013':
      // Not Printable Error. Now switched to pickup
      return {
        type: 'SWITCHEDTOPICKUP',
        retryable: error.retryable,
      };
    case 'SSR_UPSPRNTLBL_ERR_1014':
      // Label Reuse Error
      return {
        type: 'USED',
        retryable: error.retryable,
      };
    case 'SSR_UPSPRNTLBL_ERR_1015':
      // Expired Label Error
      return {
        type: 'EXPIRED',
        retryable: error.retryable,
      };
    case 'SSR_UPSPRNTLBL_ERR_1016':
      // Invalid Label Error (switched from dropoff to pickup)
      return {
        type: 'SWITCHEDTOPICKUP',
        retryable: error.retryable,
      };
    case 'SSR_UPSPRNTLBL_ERR_1017':
      // Missing Tracking ID Error
      return {
        type: 'TEMPORARY',
        retryable: error.retryable,
      };
    default:
      // Unknown Error
      return {
        type: 'UNKNOWN',
        retryable: error.retryable,
      };
  }
};

export const createLabelFromTrackingDetails = (
  trackingDetails: TrackingDetail[],
  orderId: string,
): string => {
  const base64decode = 'data:image/png;base64,';
  const conversionSrcArray = trackingDetails.map(
    (tracking) => base64decode + tracking.shippingLabel,
  );
  return createHtmlContent(conversionSrcArray, orderId);
};

const createLabelRequest = (params: PrintLabelParams) => {
  const {
    emailId,
    orderId,
    isMilitaryExchangeGuest,
    orderDetailsV2,
    returnTrackingNumbers,
  } = params;

  return {
    emailId,
    orderId,
    isGuest: isMilitaryExchangeGuest,
    net: orderDetailsV2?.net || '',
    trackingNumbers: returnTrackingNumbers,
    PrintReturnShippingLabelRequest: {
      EmailId: emailId,
      OrderId: orderId,
      TrackingNumbers: returnTrackingNumbers,
    },
  };
};

const handleLabelResponse = (
  response: AxiosResponse<IReturnShippingLabelResponse>,
  orderId: string,
): string | undefined => {
  const printLabelResponse =
    response?.data?.printReturnShippingLabelService?.printReturnLabelResponse ||
    response?.data?.PrintReturnShippingLabelService?.PrintReturnLabelResponse;

  if (!printLabelResponse) {
    throw new Error('No label found');
  }

  const base64decode = 'data:image/png;base64,';
  const conversionSrcArray = printLabelResponse.map(
    (item) => base64decode + (item.labelGraphicImage || item.LabelGraphicImage),
  );

  return createHtmlContent(conversionSrcArray, orderId);
};

const handleLabelError = (
  error: AxiosError<IGenericError>,
  setAPIError: (error: IPrintLabelError | undefined) => void,
): void => {
  const errorData = getAPIErrorData(error);

  if (errorData) {
    const errorInfo = getErrorBySSRCode(errorData);
    TagPrintReturnLabelBrowserError(errorInfo.type, error);
    setAPIError(errorInfo);
    return;
  }

  TagPrintReturnLabelBrowserError('TRYAGAIN');
  setAPIError({ type: 'TRYAGAIN', retryable: true });
};

const handleNativeAppLabel = async (
  params: PrintLabelParams,
  callbacks?: PrintLabelCallbacks,
): Promise<void> => {
  const { setRetryAvailable } = callbacks || {};

  const content = await fetchLabel(params, callbacks);
  if (content) {
    const base64Html = btoa(content);
    window?.LIFE_CYCLE_EVENT_BUS?.trigger('NATIVE_APP.PRINT_BASE64', {
      base64HtmlData: base64Html,
    });
    setRetryAvailable?.(true);
  }
};

const handleNewTabFirst = async (
  params: PrintLabelParams,
  callbacks?: PrintLabelCallbacks,
): Promise<void> => {
  const { setRetryAvailable, setAPIError } = callbacks || {};

  const newWindow = window.open(
    '',
    '_blank',
    'top=0,left=0,height=750px,width=auto,scrollbars=yes,resizable=yes',
  );
  if (!newWindow) {
    TagPrintReturnLabelBrowserError('TRYAGAIN');
    setAPIError?.({ type: 'TRYAGAIN', retryable: true });
    return;
  }

  const content = await fetchLabel(params, callbacks);
  if (!content) {
    newWindow.close();
    return;
  }

  newWindow.document.write(content);
  setRetryAvailable?.(true);
};

const fetchLabel = async (
  params: PrintLabelParams,
  callbacks?: PrintLabelCallbacks,
): Promise<string | undefined> => {
  const { trackingDetails, orderId } = params;
  const { setIsLoading, setAPIError } = callbacks || {};
  // if we have shipping label from Create Return, return that.
  if (trackingDetails) {
    return createLabelFromTrackingDetails(trackingDetails, orderId);
  }

  setIsLoading?.(true);
  setAPIError?.(undefined);

  const request = createLabelRequest(params);

  try {
    const response = await ReturnShippingLabelAPI(request);
    return handleLabelResponse(response, orderId);
  } catch (error: any) {
    if (setAPIError) handleLabelError(error, setAPIError);
    return undefined;
  } finally {
    setIsLoading?.(false);
  }
};

const openReturnLabelInNewTab = async (
  params: PrintLabelParams,
  isNativeApp: boolean,
  callbacks?: PrintLabelCallbacks,
) => {
  const { setRetryAvailable, setAPIError } = callbacks || {};

  setRetryAvailable?.(false);
  setAPIError?.(undefined);

  if (isNativeApp) {
    await handleNativeAppLabel(params, callbacks);
    return;
  }
  await handleNewTabFirst(params, callbacks);
};

// Begins the process of printing a shipping label alongside analytics tagging
export const executePrintShippingLabel = (
  params: PrintLabelParams,
  isNativeApp: boolean,
  militaryExchange: MilitaryExchange,
  callbacks?: PrintLabelCallbacks,
) => {
  const { emailId, orderId } = params;

  TagShippingLabelButtonSubmission(militaryExchange);
  if (emailId && orderId) {
    openReturnLabelInNewTab(params, isNativeApp, callbacks);
  }
};
